'use client';

import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/utils/cn';
import { useLocale, useTranslations } from 'next-intl';

// 导入纯对象，避免使用routing对象中的函数
const LOCALES = ['en', 'zh', 'pt', 'es', 'ru'];
const DEFAULT_LOCALE = 'en';

// 语言配置
const languages = [
  { value: 'en', label: 'English (US)' },
  { value: 'zh', label: '简体中文' },
  { value: 'pt', label: 'Português (Brasil)' },
  { value: 'es', label: 'Español (Latinoamérica)' },
  { value: 'ru', label: 'Русский' },
];

export default function LanguageSelector() {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const currentLocale = useLocale();
  const t = useTranslations('Dashboard.Profile');

  useEffect(() => {
    const detectCurrentLanguage = () => {
      const path = window.location.pathname;
      // 分割路径并过滤空字符串
      const segments = path.split('/').filter(Boolean);
      // 获取第一段作为可能的语言代码
      const firstSegment = segments[0] || '';

      if (LOCALES.includes(firstSegment)) {
        setValue(firstSegment);
      } else {
        setValue(DEFAULT_LOCALE);
      }
    };

    detectCurrentLanguage();
  }, []);

  const getNewPathForLanguage = (newLanguage: string) => {
    const currentPath = window.location.pathname;

    const segments = currentPath.split('/').filter(Boolean);

    const hasLocalePrefix =
      segments.length > 0 && LOCALES.includes(segments[0]);

    if (hasLocalePrefix) {
      const pathWithoutLocale =
        segments.length > 1 ? '/' + segments.slice(1).join('/') : '';

      return `/${newLanguage}${pathWithoutLocale}`;
    } else {
      return `/${newLanguage}${currentPath === '/' ? '' : currentPath}`;
    }
  };

  // 处理语言选择
  const handleLanguageSelect = (lang: string) => {
    // 计算新路径
    const newPath = getNewPathForLanguage(lang);

    // 更新状态并关闭下拉菜单
    setValue(lang);
    setOpen(false);

    // 使用新路径导航
    window.location.href = newPath;
  };

  return (
    <div className='flex flex-col gap-1'>
      <h2 className='text-base font-medium'>{t('language')}</h2>
      <div className='pt-2'>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              role='combobox'
              aria-expanded={open}
              className='w-[220px] justify-between'
            >
              <div className='flex items-center'>
                <Globe className='mr-2 h-4 w-4' />
                {value
                  ? languages.find((language) => language.value === value)
                      ?.label
                  : 'Select language...'}
              </div>
              <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-[220px] p-0'>
            <Command>
              <CommandInput placeholder='Search language...' />
              <CommandList>
                <CommandEmpty>No language found.</CommandEmpty>
                <CommandGroup>
                  {languages.map((language) => (
                    <CommandItem
                      key={language.value}
                      value={language.value}
                      onSelect={() => handleLanguageSelect(language.value)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === language.value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {language.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
